import React, { useState, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogTitle, DialogContent, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress } from '@mui/material';
import Logger from './Logger';
import { FaListUl } from "react-icons/fa";
import { getLoggerBaseURL } from '../utils/getLoggerBaseURL';

const logger = new Logger(getLoggerBaseURL());

const AssignModal = ({ showModal, onClose, oid, onStatusUpdated, selectedOrders, actionType, onAssignmentComplete, reason, fromDashboard = false }) => {
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalOrdersMap, setTotalOrdersMap] = useState(new Map()); // For assigned orders count
    const adminEmail = sessionStorage.getItem('adminEmail');
    const hasFetched = useRef(false);

    const fetchDrivers = useCallback(async () => {
        try {
            setLoading(true);
            hasFetched.current = true;

            // Fetch drivers
            const driversResponse = await fetch(`/api/drivera/${adminEmail}`);
            if (!driversResponse.ok) {
                throw new Error(`HTTP error! Status: ${driversResponse.status}`);
            }

            const driversData = await driversResponse.json();
            setDrivers(driversData);

            // Fetch all orders
            const ordersResponse = await fetch(`/api/ordera/ordersAllData/${adminEmail}`);
            if (!ordersResponse.ok) {
                throw new Error(`Failed to fetch orders`);
            }

            const ordersData = await ordersResponse.json();
            const orders = ordersData.data || [];

            // Calculate ACTIVE assigned orders only
            const ordersMap = new Map();

            driversData.forEach((driver) => {
                const activeAssigned = orders.filter((o) => {
                    const orderEmail = String(o.Driv_Email || "").trim().toLowerCase();
                    const driverEmail = String(driver.email || "").trim().toLowerCase();

                    return (
                        orderEmail === driverEmail &&
                        ["1", "4", "5"].includes(String(o.o_status))
                    );
                }).length;

                ordersMap.set(driver.email, activeAssigned);
            });

            setTotalOrdersMap(ordersMap);

        } catch (error) {
            logger.log(`Error fetching data: ${error.message}`, 'ERROR');
        } finally {
            setLoading(false);
        }
    }, [adminEmail]);

    useEffect(() => {
        if (showModal && !hasFetched.current) {
            hasFetched.current = true;
            fetchDrivers();
        }
    }, [showModal, fetchDrivers]);

    const handleClick = async (driverId) => {
        setLoading(true);
        try {
            const currentDate = new Date();
            const formattedDate = currentDate.toISOString();

            const orderIds = Array.isArray(selectedOrders) && selectedOrders.length > 0
                ? selectedOrders
                : [oid];

            await Promise.all(orderIds.map(async (orderId) => {
                const response = await fetch(`/api/ordera/driver/${orderId}/${driverId}/${adminEmail}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: actionType,
                        assignedDateTime: formattedDate,
                        ...(reason && { reason })  // Include reason only if it's provided
                    })
                });

                if (!response.ok) {
                    throw new Error(`Error updating order ${orderId}`);
                }
            }));

            onAssignmentComplete();
            onStatusUpdated();
            onClose();
        } catch (error) {
            logger.log(`Error updating details: ${error.message}`, 'ERROR');
        } finally {
            setLoading(false);
        }
    };

    const sortedDrivers = [...drivers].sort((a, b) => {
        const aOrders = totalOrdersMap.get(a.email) || 0;
        const bOrders = totalOrdersMap.get(b.email) || 0;
        return aOrders - bOrders; // least loaded first
    });

    return (
        <Dialog open={showModal} onClose={onClose} fullWidth maxWidth={false}
            PaperProps={{
                sx: {
                    width: "500px",
                    maxWidth: "2000px",
                    borderRadius: 4,
                    height: "100%",
                    maxHeight: "5000px",
                    margin: 0,
                }
            }}>
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontWeight: 700,
                    px: 3,
                    py: 2
                }}
            >
                <span style={{ flex: 1, fontSize: 20, fontWeight: 700 }}>
                    Assign Driver
                </span>

                <Button
                    onClick={onClose}
                    color="primary"
                    disabled={loading}
                    sx={{
                        minWidth: "auto",
                        ml: 2
                    }}
                >
                    X
                </Button>
            </DialogTitle>
            <DialogContent style={{ marginTop: "0px", marginBottom: "0px", paddingTop: "0px", paddingBottom: "0px" }}>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                        <CircularProgress />
                    </div>
                ) : (
                    <TableContainer
                        component={Paper}
                        style={{
                            borderRadius: "14px",
                            overflow: "hidden",
                            boxShadow: "0 8px 24px rgba(0,0,0,0.08)"
                        }}
                    >
                        <Table>
                            <TableHead style={{ background: "#fafafa" }}>
                                <TableRow>
                                    <TableCell style={{ fontWeight: 700, textAlign: "left", fontSize: 16 }}>Driver</TableCell>
                                    <TableCell style={{ fontWeight: 700, textAlign: "center", fontSize: 16 }}>Vehicle</TableCell>

                                    {!fromDashboard && (
                                        <TableCell style={{ fontWeight: 700, textAlign: "center", fontSize: 16 }}>
                                            Active Orders
                                        </TableCell>
                                    )}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {sortedDrivers.length > 0 ? (
                                    sortedDrivers.map((driver) => (
                                        <TableRow
                                            key={driver._id}
                                            hover
                                            onClick={() => handleClick(driver._id)}
                                            sx={{
                                                cursor: "pointer",
                                                transition: "all 0.25s ease",
                                                "&:hover": {
                                                    backgroundColor: "#f8fafc",
                                                    transform: "scale(1.01)"
                                                }
                                            }}
                                        >
                                            <TableCell style={{ fontWeight: 600 }}>
                                                {driver.name}
                                            </TableCell>

                                            <TableCell style={{ textAlign: "center" }}>
                                                {driver.vehicle || "N/A"}
                                            </TableCell>

                                            {!fromDashboard && (
                                                <TableCell align="center">
                                                    <span
                                                        style={{
                                                            background: "#eef2ff",
                                                            color: "#4338ca",
                                                            padding: "6px 12px",
                                                            borderRadius: "999px",
                                                            fontWeight: 700
                                                        }}
                                                    >
                                                        {totalOrdersMap.get(driver.email) || 0}
                                                    </span>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell
                                            colSpan={fromDashboard ? 2 : 3}
                                            align="center"
                                            style={{ padding: "40px" }}
                                        >
                                            <FaListUl size={36} color="#94a3b8" />
                                            <div style={{ marginTop: 12, color: "#64748b" }}>
                                                No drivers found
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default AssignModal;